// The active game system, held at module scope so both the React
// SystemContext and the axios request interceptor (which can't read React
// state) share one source of truth. Persisted in localStorage so a chosen
// system survives reloads.

const STORAGE_KEY = 'gameSystem';
export const DEFAULT_SYSTEM = 'pf-1e';

// A ?system=<slug> on the initial URL is authoritative: a shared/deep link that
// names its system must resolve under that system regardless of the viewer's
// persisted choice or the app default (GitHub issue #92). We read it once at
// module load — before any component fetches — so the axios interceptor and all
// detail fetches see the right system from the very first request. The slug is
// accepted optimistically (only a shape check here); SystemProvider corrects an
// unknown/disabled slug back to the server default once /api/systems loads.
// System slugs are lowercase and may contain digits, hyphens and dots
// (e.g. "dnd-3.5", "pf-1e"). This is a shape guard, not a whitelist — an
// unknown-but-well-formed slug is corrected by SystemProvider once /api/systems
// loads; the backend also falls back to the default for a slug it doesn't know.
const SLUG_RE = /^[a-z0-9.-]{1,40}$/;

const readUrlSystem = () => {
  try {
    const slug = new URLSearchParams(window.location.search).get('system');
    return slug && SLUG_RE.test(slug) ? slug : null;
  } catch (e) {
    return null;
  }
};

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_SYSTEM;
  } catch (e) {
    return DEFAULT_SYSTEM;
  }
};

// URL param wins over the persisted choice; persist it so it survives the
// in-place reloads the system selector triggers.
let activeSystem = readStored();
const urlSystem = readUrlSystem();
if (urlSystem && urlSystem !== activeSystem) {
  activeSystem = urlSystem;
  try {
    localStorage.setItem(STORAGE_KEY, activeSystem);
  } catch (e) {
    /* localStorage unavailable (private mode / SSR) — keep the in-memory value */
  }
}

export const getActiveSystem = () => activeSystem;

export const setActiveSystem = (slug) => {
  activeSystem = slug || DEFAULT_SYSTEM;
  try {
    localStorage.setItem(STORAGE_KEY, activeSystem);
  } catch (e) {
    /* localStorage unavailable (private mode / SSR) — keep the in-memory value */
  }
};
